import { useState, useCallback, useRef, useEffect } from "react";
import { 
  CloudArrowUp, 
  MagnifyingGlassPlus, 
  MagnifyingGlassMinus,
  ArrowsOut,
  CaretLeft,
  CaretRight,
  FilePdf,
  Image as ImageIcon,
  MapPin,
  HardDrive,
} from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../api";
import OneDriveFilePicker from "../common/OneDriveFilePicker";

const ZOOM_SECTIONS = [
  "top-left", "top-center", "top-right",
  "middle-left", "center", "middle-right",
  "bottom-left", "bottom-center", "bottom-right"
];

const SUPPORTED_FORMATS = ['.pdf', '.png', '.jpg', '.jpeg', '.webp', '.bmp', '.gif', '.tiff', '.tif', '.dwg', '.dxf'];
const ACCEPT_STRING = SUPPORTED_FORMATS.join(',');

export default function PDFViewer({ 
  sessionId, onPDFUploaded, pdfData, onPageChange,
  annotations = [], activeIssueIndex, showAnnotations, onCircleClick, onAnnotationsToggle,
  restoredPageImages = [],
  issues = [],
}) {
  // Normalize an issue entry to a string. Backend should always send strings,
  // but Gemini (vs Claude) can occasionally emit issue objects — coerce defensively
  // so .toUpperCase()/.startsWith() never crash the viewer (prompt-drift safety).
  const fmtIssue = (i) => {
    if (typeof i === "string") return i;
    if (i && typeof i === "object") {
      const sev = i.severity || i.level || "";
      const body = i.description || i.text || i.issue || i.message || i.title || "";
      return sev ? `${sev}: ${body}` : (body || JSON.stringify(i));
    }
    return i == null ? "" : String(i);
  };
  const issueAt = (idx) => fmtIssue(issues[idx] || "");
  const [uploading, setUploading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageImages, setPageImages] = useState([]);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showZoomGrid, setShowZoomGrid] = useState(false);
  const [zoomedSection, setZoomedSection] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [imgNaturalSize, setImgNaturalSize] = useState({ width: 1000, height: 1000 });
  const [showOneDrivePicker, setShowOneDrivePicker] = useState(false);
  const fileInputRef = useRef(null);

  // Severity visibility toggles — default: only critical shown
  const [visibleSeverities, setVisibleSeverities] = useState({ critical: true, major: false, minor: false });

  // Reset filter whenever a new evaluation loads (issues array changes)
  useEffect(() => {
    if (issues.length > 0) {
      setVisibleSeverities({ critical: true, major: false, minor: false });
    }
  }, [issues]);

  // When restoring from history, pre-populate pageImages with the stored image
  useEffect(() => {
    if (restoredPageImages.length > 0) {
      setPageImages(restoredPageImages);
      setCurrentPage(1);
    }
  }, [restoredPageImages]);

  const isValidFile = (filename) => {
    const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));
    return SUPPORTED_FORMATS.includes(ext);
  };

  const handleFileSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFile(file);
  }, []);  // eslint-disable-line

  // Shared upload logic used by both computer picker and OneDrive picker
  const uploadFile = useCallback(async (file) => {
    if (!isValidFile(file.name)) {
      toast.error(`Unsupported format. Accepted: ${SUPPORTED_FORMATS.join(', ')}`);
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("session_id", sessionId);

    try {
      const response = await axios.post(`${API}/upload-drawing`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data.success) {
        setPageImages([{
          page: 1,
          image_base64: response.data.first_page_image
        }]);
        setFileType(response.data.file_type);
        onPDFUploaded(response.data);

        const fileTypeLabel = response.data.file_type === 'cad' ? 'CAD drawing' : response.data.file_type === 'pdf' ? 'PDF' : 'Image';
        toast.success(`${fileTypeLabel} uploaded: ${response.data.page_count} page(s)`);
        if (response.data.conversion_warning) {
          toast.warning(`CAD conversion note: ${response.data.conversion_warning}`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
    }
  }, [sessionId, onPDFUploaded]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  }, [uploadFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleZoomSection = useCallback(async (section) => {
    try {
      const response = await axios.post(`${API}/zoom`, {
        session_id: sessionId,
        page_num: currentPage,
        section: section,
        zoom_level: 2.0
      });

      if (response.data.success) {
        setZoomedSection(section);
        setZoomedImage(response.data.image_base64);
        toast.success(`Zoomed into ${section.replace("-", " ")}`);
      }
    } catch (error) {
      toast.error("Failed to zoom into section");
    }
  }, [sessionId, currentPage]);

  const clearZoom = useCallback(() => {
    setZoomedSection(null);
    setZoomedImage(null);
    setShowZoomGrid(false);
  }, []);

  const loadPage = useCallback(async (pageNum) => {
    try {
      const response = await axios.get(`${API}/page/${sessionId}/${pageNum}`);
      if (response.data.success) {
        setPageImages(prev => {
          const existing = prev.find(p => p.page === pageNum);
          if (existing) return prev;
          return [...prev, { page: pageNum, image_base64: response.data.image_base64 }];
        });
      }
    } catch (error) {
      toast.error("Failed to load page");
    }
  }, [sessionId]);

  const handlePageChange = useCallback((newPage) => {
    if (!pdfData || newPage < 1 || newPage > pdfData.page_count) return;
    setCurrentPage(newPage);
    clearZoom();
    if (onPageChange) onPageChange(newPage);
    
    if (!pageImages.find(p => p.page === newPage)) {
      loadPage(newPage);
    }
  }, [pdfData, pageImages, loadPage, clearZoom, onPageChange]);

  const currentImage = zoomedImage || pageImages.find(p => p.page === currentPage)?.image_base64;

  // Determine icon based on file type
  const FileIcon = fileType === 'image' ? ImageIcon : FilePdf;

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200">
        <div className="flex items-center gap-2">
          <FileIcon size={20} className="text-primary" />
          <span className="font-medium text-sm text-slate-700">
            {pdfData 
              ? fileType === 'image' 
                ? "Image loaded" 
                : `Page ${currentPage} of ${pdfData.page_count}`
              : "No document loaded"}
          </span>
        </div>

        {pdfData && (
          <div className="flex items-center gap-1">
            {/* Only show page navigation for PDFs with multiple pages */}
            {fileType === 'pdf' && pdfData.page_count > 1 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="h-8 px-2"
                  data-testid="prev-page-button"
                >
                  <CaretLeft size={16} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= pdfData.page_count}
                  className="h-8 px-2"
                  data-testid="next-page-button"
                >
                  <CaretRight size={16} />
                </Button>
                
                <div className="w-px h-6 bg-slate-200 mx-2" />
              </>
            )}
            
            <Button
              variant={showZoomGrid ? "default" : "outline"}
              size="sm"
              onClick={() => setShowZoomGrid(!showZoomGrid)}
              className="h-8"
              data-testid="zoom-toggle-button"
            >
              <MagnifyingGlassPlus size={16} className="mr-1" />
              Zoom Tool
            </Button>

            {/* Annotations toggle — only shown when annotations exist */}
            {annotations.length > 0 && (
              <Button
                variant={showAnnotations ? "default" : "outline"}
                size="sm"
                onClick={onAnnotationsToggle}
                className="h-8"
                data-testid="annotations-toggle-button"
              >
                <MapPin size={16} className="mr-1" />
                {showAnnotations ? `Annotations (${annotations.length})` : "Annotations"}
              </Button>
            )}

            {/* Severity filter pills — shown when annotations + issues are present */}
            {showAnnotations && annotations.length > 0 && issues.length > 0 && (() => {
              const cnt = (prefix) =>
                annotations.filter(a => issueAt(a.issue_index).toUpperCase().startsWith(prefix)).length;
              const critCount  = cnt("CRITICAL");
              const majorCount = cnt("MAJOR");
              const minorCount = cnt("MINOR");
              const toggle = (key) =>
                setVisibleSeverities(prev => ({ ...prev, [key]: !prev[key] }));
              return (
                <div className="flex items-center gap-1 border-l border-slate-200 pl-2 ml-1">
                  {critCount > 0 && (
                    <button
                      onClick={() => toggle("critical")}
                      data-testid="toggle-critical"
                      title={visibleSeverities.critical ? "Hide critical circles" : "Show critical circles"}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold transition-all border
                        ${visibleSeverities.critical
                          ? "bg-red-100 text-red-700 border-red-300"
                          : "bg-white text-slate-400 border-slate-200 hover:border-red-200 hover:text-red-500"}`}
                    >
                      <span className="w-2 h-2 rounded-full bg-current inline-block" />
                      {critCount}
                    </button>
                  )}
                  {majorCount > 0 && (
                    <button
                      onClick={() => toggle("major")}
                      data-testid="toggle-major"
                      title={visibleSeverities.major ? "Hide major circles" : "Show major circles"}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold transition-all border
                        ${visibleSeverities.major
                          ? "bg-amber-100 text-amber-700 border-amber-300"
                          : "bg-white text-slate-400 border-slate-200 hover:border-amber-200 hover:text-amber-500"}`}
                    >
                      <span className="w-2 h-2 rounded-full bg-current inline-block" />
                      {majorCount}
                    </button>
                  )}
                  {minorCount > 0 && (
                    <button
                      onClick={() => toggle("minor")}
                      data-testid="toggle-minor"
                      title={visibleSeverities.minor ? "Hide minor circles" : "Show minor circles"}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold transition-all border
                        ${visibleSeverities.minor
                          ? "bg-blue-100 text-blue-700 border-blue-300"
                          : "bg-white text-slate-400 border-slate-200 hover:border-blue-200 hover:text-blue-500"}`}
                    >
                      <span className="w-2 h-2 rounded-full bg-current inline-block" />
                      {minorCount}
                    </button>
                  )}
                </div>
              );
            })()}
            
            {zoomedSection && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearZoom}
                className="h-8"
                data-testid="clear-zoom-button"
              >
                <ArrowsOut size={16} className="mr-1" />
                Full View
              </Button>
            )}
          </div>
        )}
      </div>

      {/* PDF Content Area */}
      <div 
        className="flex-1 relative overflow-auto p-6"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        {/* When pdfData is set but no page image yet (history restore with unavailable image) */}
        {pdfData && pageImages.length === 0 && restoredPageImages.length === 0 ? (
          <div className="h-full flex items-center justify-center bg-slate-100">
            <div className="text-center text-slate-400 space-y-2">
              <div className="text-4xl opacity-30">📄</div>
              <p className="text-sm font-medium">Drawing image unavailable</p>
              <p className="text-xs">The original file could not be loaded from storage.</p>
            </div>
          </div>
        ) : !pdfData ? (
          /* Upload Dropzone */
          <div 
            className="h-full flex items-center justify-center"
            data-testid="pdf-upload-dropzone"
          >
            <div className="relative w-full max-w-lg">
              <div className="empty-state-bg rounded-sm" />
              <div 
                className="relative border-2 border-dashed border-slate-300 hover:border-primary rounded-sm p-10 text-center bg-white/90"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT_STRING}
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="pdf-file-input"
                />
                
                <div className="flex flex-col items-center">
                  {uploading ? (
                    <>
                      <div className="spinner mb-4" />
                      <p className="text-slate-600 font-medium">Uploading file...</p>
                    </>
                  ) : (
                    <>
                      <CloudArrowUp size={44} className="text-slate-400 mb-3" />
                      <p className="text-slate-800 font-semibold mb-1">
                        Upload Technical Drawing
                      </p>
                      <p className="text-xs text-slate-400 mb-6">
                        Drag and drop a file here, or choose a source below
                      </p>

                      {/* Two-option buttons */}
                      <div className="flex items-center gap-3 w-full max-w-xs">
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="flex-1 flex flex-col items-center gap-2 px-4 py-3 border border-slate-200 rounded-sm hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer"
                          data-testid="browse-files-button"
                        >
                          <HardDrive size={20} className="text-slate-400" />
                          <span className="text-xs font-medium text-slate-600">From Computer</span>
                        </button>

                        <button
                          onClick={() => setShowOneDrivePicker(true)}
                          className="flex-1 flex flex-col items-center gap-2 px-4 py-3 border border-slate-200 rounded-sm hover:border-[#0078D4] hover:bg-[#0078D4]/5 transition-colors cursor-pointer"
                          data-testid="onedrive-pick-button"
                        >
                          {/* OneDrive icon */}
                          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
                            <path d="M9.5 7.5C10.83 5.3 13.28 4 16 4C19.87 4 23 7.13 23 11C23 11.34 22.97 11.68 22.93 12C23.56 12.35 24 13.03 24 13.83C24 14.92 23.09 15.83 22 15.83H8.5C6.57 15.83 5 14.26 5 12.33C5 10.51 6.38 9.02 8.17 8.86C8.31 8.23 8.85 7.73 9.5 7.5Z" fill="#0078D4" opacity="0.6" />
                            <path d="M3.5 11C1.57 11 0 12.57 0 14.5C0 16.43 1.57 18 3.5 18H19.5C20.88 18 22 16.88 22 15.5C22 14.12 20.88 13 19.5 13H19C18.45 10.2 16 8 13 8C10.73 8 8.73 9.22 7.69 11.06C6.7 10.42 5.15 10.5 4.5 11H3.5Z" fill="#0078D4" />
                          </svg>
                          <span className="text-xs font-medium text-slate-600">From OneDrive</span>
                        </button>
                      </div>

                      <p className="text-[10px] text-slate-300 mt-4 font-mono">
                        PDF, PNG, JPG, JPEG, WebP, BMP, GIF, TIFF
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Drawing Image Display */
          <div className="flex items-center justify-center min-h-full">
            <div 
              className="relative bg-white shadow-lg pdf-page"
              style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'center' }}
            >
              {currentImage && (
                <img
                  src={`data:image/${currentImage.startsWith('/9j/') ? 'jpeg' : 'png'};base64,${currentImage}`}
                  alt={`${fileType === 'image' ? 'Drawing' : `Page ${currentPage}`}${zoomedSection ? ` - ${zoomedSection}` : ''}`}
                  className="max-w-full h-auto block"
                  data-testid="pdf-image"
                  onLoad={(e) => setImgNaturalSize({
                    width: e.target.naturalWidth,
                    height: e.target.naturalHeight
                  })}
                />
              )}

              {/* Annotation circles SVG overlay */}
              {showAnnotations && !zoomedSection && annotations.length > 0 && currentImage && (
                <svg
                  viewBox={`0 0 ${imgNaturalSize.width} ${imgNaturalSize.height}`}
                  preserveAspectRatio="none"
                  style={{
                    position: 'absolute',
                    top: 0, left: 0,
                    width: '100%', height: '100%',
                    overflow: 'visible'
                  }}
                >
                  {annotations.map((ann, idx) => {
                    // Severity filter — skip if this severity is toggled off
                    if (issues.length > 0) {
                      const t = issueAt(ann.issue_index).toUpperCase();
                      const isCrit  = t.startsWith("CRITICAL");
                      const isMaj   = t.startsWith("MAJOR");
                      const isMin   = t.startsWith("MINOR");
                      if (isCrit  && !visibleSeverities.critical) return null;
                      if (isMaj   && !visibleSeverities.major)    return null;
                      if (isMin   && !visibleSeverities.minor)    return null;
                    }
                    const cx = ann.x * imgNaturalSize.width;
                    const cy = ann.y * imgNaturalSize.height;
                    const r  = ann.radius * imgNaturalSize.width;
                    const isActive = activeIssueIndex === ann.issue_index;
                    const num = idx + 1;
                    const badgeR = r * 0.32;
                    const badgeX = cx + r * 0.65;
                    const badgeY = cy - r * 0.65;

                    // Colour by severity
                    const issueText = issueAt(ann.issue_index).toUpperCase();
                    const isCritical = issueText.startsWith("CRITICAL");
                    const isMajor    = issueText.startsWith("MAJOR");
                    // Critical=red, Major=amber, Minor/unknown=blue
                    const normalColor = isCritical ? '#ef4444' : isMajor ? '#f59e0b' : '#3b82f6';
                    const activeColor = isCritical ? '#dc2626' : isMajor ? '#d97706' : '#2563eb';
                    const fillNormal  = isCritical ? 'rgba(239,68,68,0.08)'   : isMajor ? 'rgba(245,158,11,0.08)'  : 'rgba(59,130,246,0.08)';
                    const fillActive  = isCritical ? 'rgba(220,38,38,0.18)'   : isMajor ? 'rgba(217,119,6,0.18)'   : 'rgba(37,99,235,0.18)';

                    return (
                      <g
                        key={idx}
                        onClick={() => onCircleClick && onCircleClick(ann.issue_index)}
                        style={{ cursor: 'pointer' }}
                        data-testid={`annotation-circle-${idx}`}
                      >
                        {/* Main circle */}
                        <circle
                          cx={cx} cy={cy} r={r}
                          fill={isActive ? fillActive : fillNormal}
                          stroke={isActive ? activeColor : normalColor}
                          strokeWidth={r * (isActive ? 0.12 : 0.08)}
                          strokeDasharray={isActive ? undefined : `${r * 0.25} ${r * 0.12}`}
                        />
                        {/* Badge circle */}
                        <circle
                          cx={badgeX} cy={badgeY} r={badgeR}
                          fill={isActive ? activeColor : normalColor}
                        />
                        {/* Badge number */}
                        <text
                          x={badgeX} y={badgeY}
                          textAnchor="middle"
                          dominantBaseline="central"
                          fill="white"
                          fontSize={badgeR * 1.2}
                          fontWeight="bold"
                          style={{ userSelect: 'none', fontFamily: 'sans-serif' }}
                        >
                          {num}
                        </text>
                      </g>
                    );
                  })}
                </svg>
              )}
              
              {/* Zoom Grid Overlay */}
              {showZoomGrid && !zoomedSection && (
                <div className="zoom-overlay">
                  <div className="zoom-grid">
                    {ZOOM_SECTIONS.map((section) => (
                      <div
                        key={section}
                        className="zoom-section flex items-center justify-center"
                        onClick={() => handleZoomSection(section)}
                        data-testid={`zoom-section-${section}`}
                      >
                        <span className="bg-primary/80 text-white text-xs px-2 py-1 rounded opacity-0 hover:opacity-100 transition-opacity font-medium">
                          {section.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Zoom Controls */}
      {pdfData && (
        <div className="pdf-controls">
          <div className="bg-white border border-slate-200 shadow-lg rounded-sm flex items-center gap-1 p-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setZoomLevel(z => Math.max(0.5, z - 0.25))}
              className="h-8 w-8 p-0"
              data-testid="zoom-out-button"
            >
              <MagnifyingGlassMinus size={18} />
            </Button>
            <span className="text-xs font-mono text-slate-600 w-12 text-center">
              {Math.round(zoomLevel * 100)}%
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setZoomLevel(z => Math.min(3, z + 0.25))}
              className="h-8 w-8 p-0"
              data-testid="zoom-in-button"
            >
              <MagnifyingGlassPlus size={18} />
            </Button>
          </div>
        </div>
      )}

      {/* OneDrive File Picker */}
      {showOneDrivePicker && (
        <OneDriveFilePicker
          onSelect={(file) => uploadFile(file)}
          onClose={() => setShowOneDrivePicker(false)}
        />
      )}
    </div>
  );
}
